import type { Session } from "@supabase/supabase-js";
import { Fragment, useEffect, useState } from "react";
import { AdminAccess } from "./AdminAccess";
import { EventAdminEditor } from "./EventAdminEditor";
import { EventCodeManager } from "./EventCodeManager";
import { EventRegistrationOperations } from "./EventRegistrationOperations";
import { getAdminSupabaseClient, readAdminSession, signOutAdmin } from "./lib/adminRepository";
import {
  type BookingEvent,
  type EventAdminSnapshot,
  type EventOperationResult,
  loadEventAdminSnapshot,
} from "./lib/eventAdminRepository";
import "./admin-workspace.css";
import "./event-admin-workspace.css";

export function EventAdminWorkspaceAccess() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    readAdminSession()
      .then((value) => active && setSession(value))
      .finally(() => active && setLoading(false));
    const { data } = getAdminSupabaseClient().auth.onAuthStateChange((_event, nextSession) => {
      if (active) {
        setSession(nextSession);
        setLoading(false);
      }
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  if (loading) return <div className="workspace-loading">Veranstaltungen werden geöffnet …</div>;
  if (!session) return <AdminAccess />;
  return <EventAdminWorkspace />;
}

function EventAdminWorkspace() {
  const [snapshot, setSnapshot] = useState<EventAdminSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPastEvents, setShowPastEvents] = useState(false);

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      setSnapshot(await loadEventAdminSnapshot());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Veranstaltungen konnten nicht geladen werden.");
    } finally {
      setBusy(false);
    }
  }

  async function operationCompleted(result: EventOperationResult) {
    setMessage(result.message);
    setWarning(result.warning ?? null);
    setError(null);
    await refresh();
  }

  async function editorSaved(savedMessage: string) {
    setMessage(savedMessage);
    setWarning(null);
    setError(null);
    await refresh();
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: initial event load intentionally runs once.
  useEffect(() => {
    void refresh();
  }, []);

  const allEvents = snapshot?.events ?? [];
  const now = Date.now();
  const currentEvents = [...allEvents]
    .filter((event) => !isArchivedEvent(event, now))
    .sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt));
  const archivedEvents = [...allEvents]
    .filter((event) => isArchivedEvent(event, now))
    .sort((left, right) => Date.parse(right.startsAt) - Date.parse(left.startsAt));
  const visibleEvents = showPastEvents ? [...currentEvents, ...archivedEvents] : currentEvents;
  const adminActions = snapshot?.actions ?? [];

  return (
    <main className="workspace-shell event-admin-shell">
      <header className="workspace-header event-admin-header">
        <div className="event-admin-header__copy">
          <p className="workspace-kicker">Katrin & Rayk Booking</p>
          <h1>Veranstaltungen</h1>
          <p className="workspace-lead">Teilnehmer, Plätze, Zahlungen, Einladungen und Kommunikation an einem Ort.</p>
        </div>
        <div className="workspace-header-actions event-admin-header__actions">
          <a className="event-admin-link event-admin-link--primary" href="/?admin=events&new=1#event-editor">
            + Neue Veranstaltung
          </a>
          <button type="button" onClick={() => void refresh()} disabled={busy}>
            {busy && snapshot ? "Lädt …" : "Neu laden"}
          </button>
          <button type="button" onClick={() => void signOutAdmin()}>Abmelden</button>
        </div>
      </header>

      {message ? <div className="workspace-notice success">{message}</div> : null}
      {warning ? <div className="workspace-notice warning">{warning}</div> : null}
      {error ? (
        <div className="workspace-notice error event-admin-load-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => void refresh()} disabled={busy}>{busy ? "Neuer Versuch …" : "Erneut laden"}</button>
        </div>
      ) : null}

      <details id="event-editor" className="event-admin-editor-disclosure" open={new URLSearchParams(window.location.search).get("new") === "1"}>
        <summary>+ Veranstaltung erstellen oder bearbeiten</summary>
        <EventAdminEditor events={snapshot?.events ?? []} onSaved={editorSaved} />
      </details>

      <section className="event-admin-list" aria-label="Veranstaltungen">
        {snapshot && allEvents.length > 0 ? (
          <div className="event-admin-list-toolbar">
            <span>
              <strong>{currentEvents.length}</strong>{" "}
              {currentEvents.length === 1 ? "aktuelle Veranstaltung" : "aktuelle Veranstaltungen"}
            </span>
            {archivedEvents.length > 0 ? (
              <button type="button" onClick={() => setShowPastEvents((value) => !value)}>
                {showPastEvents
                  ? "Vergangene ausblenden"
                  : `Vergangene / abgesagte anzeigen (${archivedEvents.length})`}
              </button>
            ) : null}
          </div>
        ) : null}

        {visibleEvents.map((event, index) => {
          const archived = isArchivedEvent(event, now);
          const previousEvent = index > 0 ? visibleEvents[index - 1] : undefined;
          const previousArchived = previousEvent ? isArchivedEvent(previousEvent, now) : false;
          const activeRegistrations = event.registrations.filter(
            (registration) => registration.registrationStatus !== "cancelled",
          );
          const waitlistCount = activeRegistrations.filter(
            (registration) => registration.registrationStatus === "waitlist",
          ).length;
          const paidRevenue = activeRegistrations
            .filter((registration) => registration.paymentStatus === "paid")
            .reduce((sum, registration) => sum + (registration.amountCents ?? 0), 0);
          const openRevenue = activeRegistrations
            .filter(
              (registration) =>
                registration.registrationStatus !== "waitlist" &&
                ["unpaid", "pending", "failed"].includes(registration.paymentStatus),
            )
            .reduce((sum, registration) => sum + (registration.amountCents ?? 0), 0);
          const currency = event.roles[0]?.currency ?? "EUR";

          return (
            <Fragment key={event.id}>
              {archived && !previousArchived ? (
                <div className="event-admin-archive-label">Vergangene / abgesagte</div>
              ) : null}
              <details
                className={archived ? "event-admin-event-card is-archived" : "event-admin-event-card"}
              >
              <summary className="event-admin-event-summary">
                <span>
                  <strong>{event.title}</strong>
                  <small>{formatEventDate(event.startsAt)} · {event.venueName || event.city || "Ort noch offen"}</small>
                </span>
                <span className="event-admin-event-status">{statusLabel(event.status)}</span>
              </summary>
              <div className="event-admin-event-body">
                <p className="workspace-kicker">{event.eyebrow || statusLabel(event.status)}</p>
                <h2>{event.title}</h2>
                <p>{event.summary || "Noch ohne Kurzbeschreibung."}</p>
                <div className="event-admin-event-meta">
                  <span>{formatEventDate(event.startsAt)}</span>
                  <span>{event.venueName || event.city || "Ort noch offen"}</span>
                  <span>{statusLabel(event.status)}</span>
                </div>
                {event.status === "published" && event.slug ? (
                  <div className="event-admin-share-actions">
                    <button type="button" onClick={() => void copyPublicEventLink(event)}>Öffentlichen Link kopieren</button>
                    <a href={`/veranstaltungen/${encodeURIComponent(event.slug)}`} target="_blank" rel="noreferrer">Öffentliche Seite öffnen ↗</a>
                  </div>
                ) : null}

                <div className="event-admin-overview">
                  <div><strong>{activeRegistrations.length}</strong><span>Anmeldungen</span></div>
                  <div><strong>{formatPrice(paidRevenue, currency)}</strong><span>bezahlt</span></div>
                  <div><strong>{formatPrice(openRevenue, currency)}</strong><span>offen</span></div>
                  <div><strong>{waitlistCount}</strong><span>Warteliste</span></div>
                </div>

                <div className="event-role-list">
                  {event.roles.map((role) => (
                    <div className="event-role-card" key={role.id}>
                      <div>
                        <strong>{role.label}</strong>
                        <small>{role.confirmedCount} belegt{role.waitlistCount > 0 ? ` · ${role.waitlistCount} Warteliste` : ""}</small>
                      </div>
                      <span>{role.capacity ? `${role.capacity} Plätze` : "Ohne feste Kapazität"} · {formatPrice(role.priceCents, role.currency)} · {paymentPolicyLabel(role.paymentPolicy)}</span>
                    </div>
                  ))}
                </div>

                <EventCodeManager eventId={event.id} roles={event.roles} />

                <section className="event-admin-participants" aria-label="Teilnehmerliste">
                  <header>
                    <div>
                      <p className="workspace-kicker">Teilnehmerliste</p>
                      <h3>{activeRegistrations.length} aktuelle Anmeldungen</h3>
                    </div>
                    <div className="event-admin-list-actions">
                      <button type="button" onClick={() => void copyParticipantEmails(event)} disabled={activeRegistrations.length === 0}>E-Mails kopieren</button>
                      <button type="button" onClick={() => window.print()} disabled={activeRegistrations.length === 0}>Anwesenheitsliste drucken</button>
                      <button type="button" onClick={() => downloadParticipantCsv(event)} disabled={activeRegistrations.length === 0}>CSV exportieren</button>
                    </div>
                  </header>

                  {activeRegistrations.length === 0 ? (
                    <p className="event-admin-empty">Keine aktuellen Anmeldungen.</p>
                  ) : (
                    <div className="event-admin-registration-list">
                      {activeRegistrations.map((registration) => (
                        <EventRegistrationOperations actions={adminActions} event={event} key={registration.id} onChanged={operationCompleted} registration={registration} />
                      ))}
                    </div>
                  )}
                </section>
              </div>
              </details>
            </Fragment>
          );
        })}
        {snapshot && allEvents.length === 0 ? <section className="workspace-panel"><p>Noch keine Veranstaltungen angelegt. Oben kann direkt der erste Entwurf entstehen.</p></section> : null}
        {!snapshot ? (
          <section className="workspace-panel event-admin-loading-panel" aria-live="polite">
            <strong>{busy ? "Veranstaltungen werden geladen …" : "Keine Daten verfügbar."}</strong>
            {busy ? <span>Teilnehmer und Zahlungen werden zusammengeführt.</span> : null}
          </section>
        ) : null}
      </section>
    </main>
  );
}

function isArchivedEvent(event: BookingEvent, now = Date.now()) {
  return (
    event.status === "cancelled" ||
    event.status === "completed" ||
    Date.parse(event.endsAt) < now
  );
}

async function copyPublicEventLink(event: BookingEvent) {
  const url = `${window.location.origin}/veranstaltungen/${encodeURIComponent(event.slug)}`;
  try {
    await navigator.clipboard.writeText(url);
    window.alert("Der öffentliche Veranstaltungslink wurde kopiert.");
  } catch {
    window.prompt("Öffentlichen Link kopieren:", url);
  }
}

async function copyParticipantEmails(event: BookingEvent) {
  const emails = [...new Set(event.registrations.filter((registration) => registration.registrationStatus !== "cancelled").map((registration) => registration.email).filter(Boolean))].join(", ");
  if (!emails) return;
  try {
    await navigator.clipboard.writeText(emails);
    window.alert("Die E-Mail-Adressen wurden in die Zwischenablage kopiert.");
  } catch {
    window.prompt("E-Mail-Adressen kopieren:", emails);
  }
}

function downloadParticipantCsv(event: BookingEvent) {
  const header = ["Vorname", "Nachname", "Teilnahmeform", "E-Mail", "Telefon", "Anmeldestatus", "Zahlungsstatus", "Zahlungswahl", "Zahlungsmethode", "Anbieter", "Zahlungsfrist", "Betrag", "Referenz", "Angemeldet am", "Bezahlt am", "Nachricht"];
  const rows = event.registrations
    .filter((registration) => registration.registrationStatus !== "cancelled")
    .map((registration) => {
    const role = event.roles.find((candidate) => candidate.id === registration.eventRoleId);
    return [registration.firstName, registration.lastName, role?.label || "", registration.email, registration.phone, registrationStatusLabel(registration.registrationStatus), paymentStatusLabel(registration.paymentStatus), paymentChoiceLabel(registration.paymentChoice), paymentMethodLabel(registration.paymentMethod), registration.paymentProvider || "", registration.paymentDueAt ? formatDateTime(registration.paymentDueAt) : "", formatPrice(registration.amountCents, registration.currency), registration.reference, formatDateTime(registration.createdAt), registration.paidAt ? formatDateTime(registration.paidAt) : "", registration.note];
  });
  const csv = [header, ...rows].map((row) => row.map((value) => csvCell(value)).join(";")).join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${event.title.toLowerCase().replace(/[^a-z0-9äöüß]+/gi, "-")}-teilnehmer.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function csvCell(value: string) { return `"${value.replaceAll('"', '""')}"`; }
function formatEventDate(value: string) { if (!value) return "Termin noch offen"; return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)); }
function formatDateTime(value: string) { if (!value) return "–"; return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)); }
function formatPrice(value: number | null, currency: string) { if (value === null) return "Preis noch offen"; if (value === 0) return "Kostenfrei"; return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(value / 100); }
function statusLabel(status: string) { if (status === "published") return "Veröffentlicht"; if (status === "cancelled") return "Abgesagt"; if (status === "completed") return "Abgeschlossen"; return "Entwurf"; }
function registrationStatusLabel(status: string) { if (status === "confirmed") return "Bestätigt"; if (status === "pending") return "Reserviert"; if (status === "waitlist") return "Warteliste"; if (status === "cancelled") return "Storniert"; return "Offen"; }
function paymentStatusLabel(status: string) { if (status === "paid") return "Bezahlt"; if (status === "pending") return "Zahlung läuft"; if (status === "not_required") return "Keine Zahlung"; if (status === "refunded") return "Erstattet"; if (status === "failed") return "Fehlgeschlagen"; return "Offen"; }
function paymentChoiceLabel(choice: string) { if (choice === "online") return "Online"; if (choice === "invoice") return "Rechnung"; if (choice === "cash") return "Barzahlung vor Ort"; return "Keine Zahlung"; }
function paymentMethodLabel(method: string) { const labels: Record<string, string> = { card: "Karte", klarna: "Klarna", paypal: "PayPal", sepa_debit: "SEPA-Lastschrift", bank_transfer: "Überweisung" }; return labels[method] ?? method; }
function paymentPolicyLabel(policy: string) { if (policy === "online_required") return "Onlinezahlung"; if (policy === "invoice_required") return "Rechnung"; if (policy === "free") return "kostenfrei"; return "Online oder Rechnung"; }
